import type { ChatMessage, ChatPart } from '@core/tellent-assistant-types';

type TextPart = Extract<ChatPart, { kind: 'text' }>;
type ToolCallPart = Extract<ChatPart, { kind: 'tool-call' }>;
type ToolResultPart = Extract<ChatPart, { kind: 'tool-result' }>;

export interface TimelineToolEvent {
  kind: 'tool';
  toolCallId: string;
  name: string;
  status: ToolCallPart['status'];
  hasResult: boolean;
  stepNumber: number;
}

export interface TimelineReasoningEvent {
  kind: 'reasoning';
  messageId: string;
  content: string;
  done: boolean;
}

export type TimelineEvent = TimelineToolEvent | TimelineReasoningEvent;

export type BodyEntry =
  | { kind: 'text'; part: TextPart }
  | { kind: 'render-tool'; part: ToolCallPart };

export interface AssistantMessageView {
  timeline: TimelineEvent[];
  body: BodyEntry[];
}

export const splitAssistantMessage = (
  msg: ChatMessage,
  renderToolNames: ReadonlySet<string>,
): AssistantMessageView => {
  if (msg.role !== 'assistant') return { timeline: [], body: [] };

  const resultIds = new Set(
    msg.parts
      .filter((part): part is ToolResultPart => part.kind === 'tool-result')
      .map(part => part.toolCallId),
  );

  const isTimelineTool = (part: ChatPart): part is ToolCallPart =>
    part.kind === 'tool-call' && !renderToolNames.has(part.name);

  const stepNumberByCallId = new Map(
    msg.parts.filter(isTimelineTool).map((part, index) => [part.toolCallId, index + 1]),
  );

  const body = msg.parts.flatMap((part): BodyEntry[] => {
    if (part.kind === 'text') return [{ kind: 'text', part }];
    if (part.kind === 'tool-call' && renderToolNames.has(part.name)) {
      return [{ kind: 'render-tool', part }];
    }
    return [];
  });

  const timeline = msg.parts.flatMap((part): TimelineEvent[] => {
    if (part.kind === 'reasoning') {
      return [
        {
          kind: 'reasoning',
          messageId: part.messageId,
          content: part.content,
          done: part.done,
        },
      ];
    }
    if (isTimelineTool(part)) {
      return [
        {
          kind: 'tool',
          toolCallId: part.toolCallId,
          name: part.name,
          status: part.status,
          hasResult: resultIds.has(part.toolCallId),
          stepNumber: stepNumberByCallId.get(part.toolCallId)!,
        },
      ];
    }
    return [];
  });

  return { timeline, body };
};
